import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ClearStateAction,
  DeliveryLocationAction,
  getChooseLocationAction,
  getLocationMenusAction,
  getLocationTimeslotAction,
  updateBackBtnValueSuccessAction,
  SetSelectedLocationAction,
  SetSelectedTimeslotAction,
  setTimeslotExpiredAction,
  setAcceptedAgeRestrictionYears,
  removeAcceptedAgeRestrictionYear,
} from './choose-location.actions';
import { IChooseLocationFeature } from './choose-location.feature.state';
import {
  getChooseLocation,
  getDeliveryLocation,
  getFailures,
  getIsLoading,
  getIsMenuLoading,
  getLocationMenus,
  getLocationTimeslot,
  updateBackBtnValue,
  getSelectedLocation,
  getSelectedTimeslot,
  getIsTimeslotExpired,
  getLocationPropTypes,
  getAcceptedAgeRestrictionYears,
} from './choose-location.selectors';
import { filter, map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { ChooseLocation, Timeslot } from '../models';
import { DateHelper } from '@symphony-solo/shared/utils/src';

@Injectable()
export class ChooseLocationFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public isMenuLoading$ = this.store.select(getIsMenuLoading);
  public failures$ = this.store.select(getFailures);
  public chooseLocationBehavior = new BehaviorSubject<any>(null);
  public chooseLocation$ = this.store.select(getChooseLocation).pipe(filter((data) => data !== null && data !== undefined));

  public locationTimeSlots$ = this.store.select(getLocationTimeslot).pipe(filter((data) => data !== null && data !== undefined));
  public locationMenus$ = this.store.select(getLocationMenus);
  public deliveryLocation$ = this.store.select(getDeliveryLocation);
  public selectedLocation$ = this.store.select(getSelectedLocation);
  public selectedTimeslot$ = this.store.select(getSelectedTimeslot);
  public backBtnValue$ = this.store.select(updateBackBtnValue);
  public locationPropTypes$ = this.store.select(getLocationPropTypes);

  public orderData$ = combineLatest([this.selectedLocation$, this.selectedTimeslot$]).pipe(
    map(([orderLocation, timeSlot]) => {
      if (orderLocation && timeSlot) {
        return {
          timeSlot,
          orderLocation,
        };
      }
    })
  );

  public isLocationAllowGuestCheckout$ = this.selectedLocation$.pipe(
    map((selectedLocation) => {
      return selectedLocation?.guestCheckout ? selectedLocation.guestCheckout : false;
    })
  );

  public selectedLocationPageTitle$ = this.selectedLocation$.pipe(
    map((selectedLocation) => {
      return `Ordering from<br/><small>${selectedLocation.name}</small>`;
    })
  );

  public collectionTime$ = this.selectedTimeslot$.pipe(
    map((selectedTimeslot) => {
      if (selectedTimeslot) {
        const dateValue = DateHelper.getLocalDateByDMY(selectedTimeslot.start);
        const startTime = DateHelper.extractTime(selectedTimeslot.start);
        const endTime = DateHelper.extractTime(selectedTimeslot.end);

        if (DateHelper.getLocalDateByDMY(selectedTimeslot.start) === DateHelper.getLocalDateByDMY(new Date().toISOString())) {
          return `Today at  ${startTime} - ${endTime}`;
        }

        return `${dateValue} at ${startTime} - ${endTime}`;
      }
    })
  );

  public isTimeslotExpired$ = this.store.select(getIsTimeslotExpired).pipe(
    map((isTimeslotExpired) => {
      return !!isTimeslotExpired;
    })
  );

  public acceptedAgeRestrictionYears$ = this.store.select(getAcceptedAgeRestrictionYears).pipe(
    map((acceptedAgeRestrictionYears) => {
      return acceptedAgeRestrictionYears ? acceptedAgeRestrictionYears : [];
    })
  );

  constructor(private store: Store<IChooseLocationFeature>) {}

  public getChooseLocation(): void {
    this.store.dispatch(getChooseLocationAction());
  }

  public getLocationTimeslot(payload: any): void {
    this.store.dispatch(getLocationTimeslotAction(payload));
  }

  public getLocationMenus(payload: any): void {
    this.store.dispatch(getLocationMenusAction(payload));
  }

  public getDeliveryLocation(payload: any): void {
    this.store.dispatch(DeliveryLocationAction(payload));
  }

  public updateBackBtnValue(btnValue: string): void {
    this.store.dispatch(updateBackBtnValueSuccessAction({ btnValue }));
  }

  public clearState(actionType: string): void {
    this.store.dispatch(ClearStateAction({ actionType }));
  }

  public setSelectedLocation(selectedLocation: ChooseLocation): void {
    this.store.dispatch(SetSelectedLocationAction({ selectedLocation }));
  }

  public setSelectedTimeslot(selectedTimeslot: Timeslot): void {
    this.store.dispatch(SetSelectedTimeslotAction({ selectedTimeslot }));
  }

  public setTimeslotExpired(isTimeslotExpired: boolean): void {
    this.store.dispatch(setTimeslotExpiredAction({ isTimeslotExpired }));
  }

  public setAcceptedAgeRestrictionYears(year: number): void {
    this.store.dispatch(setAcceptedAgeRestrictionYears({ year }));
  }

  public removeAcceptedAgeRestrictionYear(year: number): void {
    this.store.dispatch(removeAcceptedAgeRestrictionYear({ year }));
  }
}
